import { ArrowUpIcon } from "@phosphor-icons/react/dist/csr/ArrowUp";
import { useRef, useState, type FormEvent, type KeyboardEvent } from "react";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupTextarea,
} from "@volca/ui/components/ui/input-group";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@volca/ui/components/ui/tooltip";
import { TooltipShortcut } from "@volca/ui/components/ui/tooltip-shortcut";
import { formatShortcutAriaLabel } from "@volca/ui/lib/desktop-platform";
import { toast } from "@volca/ui/lib/toast";
import { getErrorMessage } from "@/core/api/errors";

const MAX_MESSAGE_LENGTH = 5000;
const COUNTER_VISIBLE_FROM = MAX_MESSAGE_LENGTH - 500;
const COMPOSER_MAX_HEIGHT_PX = 148;
const SEND_SHORTCUT = ["mod", "Enter"] as const;

const fitToContent = (field: HTMLTextAreaElement | null) => {
  if (!field) return;
  field.style.height = "auto";
  field.style.height = `${Math.min(field.scrollHeight, COMPOSER_MAX_HEIGHT_PX)}px`;
};

/** A compact reply field that grows until it reaches six lines. */
export const PackChatReplyForm = ({
  loading,
  reopens,
  placeholder,
  actionLabel,
  autoFocus,
  onReply,
}: {
  loading?: boolean;
  reopens?: boolean;
  placeholder?: string;
  actionLabel?: string;
  autoFocus?: boolean;
  onReply: (body: string) => Promise<void>;
}) => {
  const [body, setBody] = useState("");
  const field = useRef<HTMLTextAreaElement>(null);
  const trimmed = body.trim();

  const send = async () => {
    if (!trimmed || loading) return;
    try {
      await onReply(trimmed);
      setBody("");
      if (field.current) field.current.style.height = "auto";
    } catch (error) {
      toast.error("Message not sent", { description: getErrorMessage(error) });
    }
  };

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    void send();
  };

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key !== "Enter" || !(event.metaKey || event.ctrlKey)) return;
    event.preventDefault();
    void send();
  };

  const action = actionLabel ?? (reopens ? "Reply and reopen" : "Send reply");
  const sendShortcutAriaLabel = formatShortcutAriaLabel(SEND_SHORTCUT);

  return (
    <form onSubmit={submit} className="w-full">
      <Stack className="w-full" gap={1} align="stretch">
        <InputGroup className="items-end bg-input">
          <InputGroupTextarea
            ref={field}
            rows={1}
            aria-label="Reply message"
            value={body}
            maxLength={MAX_MESSAGE_LENGTH}
            autoFocus={autoFocus}
            placeholder={
              placeholder ??
              (reopens ? "Reply to reopen this thread" : "Write a reply")
            }
            disabled={loading}
            className="field-sizing-fixed min-h-0 py-2.5 pl-3 text-sm leading-6"
            onKeyDown={onKeyDown}
            onChange={(event) => {
              setBody(event.target.value);
              fitToContent(event.target);
            }}
          />
          <InputGroupAddon align="inline-end" className="py-2">
            <Tooltip>
              <TooltipTrigger
                render={<span className="inline-flex shrink-0" />}
              >
                <InputGroupButton
                  type="submit"
                  size="icon-sm"
                  variant="default"
                  aria-label={`${action} (${sendShortcutAriaLabel})`}
                  disabled={!trimmed || loading}
                  className="rounded-full"
                >
                  <ArrowUpIcon aria-hidden />
                </InputGroupButton>
              </TooltipTrigger>
              <TooltipContent side="top">
                <TooltipShortcut keys={SEND_SHORTCUT}>{action}</TooltipShortcut>
              </TooltipContent>
            </Tooltip>
          </InputGroupAddon>
        </InputGroup>
        {body.length >= COUNTER_VISIBLE_FROM && (
          <Text variant="caption" tone="muted">
            {MAX_MESSAGE_LENGTH - body.length} characters left
          </Text>
        )}
      </Stack>
    </form>
  );
};
